<template>
  <artmisads-modal
    v-model:open="visible"
    :title="title"
    :width="width"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="payment-form">
      <a-form :model="form" layout="vertical" @finish="handleSubmit">
        <a-form-item 
          :label="amountLabel" 
          :required="true"
          :validate-status="amountError ? 'error' : ''"
          :help="amountError"
        >
          <artmisads-input
            v-model:value="form.paymentAmount"
           
            type="number"
            :min="0"
            step="0.01"
            @blur="validateAmount"
          />
        </a-form-item>
        
        <a-form-item 
          :label="dateLabel" 
          :required="true"
          :validate-status="dateError ? 'error' : ''"
          :help="dateError"
        >
          <artemisads-date-picker
            v-model:value="form.paymentDate"
           
            style="width: 100%"
            @change="validateDate"
          />
        </a-form-item>
        
        <div class="form-actions">
          <artmisads-button @click="handleCancel" style="margin-right: 12px;">
            {{ cancelText }}
          </artmisads-button>
          <artmisads-button type="primary" html-type="submit" :loading="loading">
            {{ submitText }}
          </artmisads-button>
        </div>
      </a-form>
    </div>
  </artmisads-modal>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import { message } from 'ant-design-vue'
import { financeApi } from '~/server/operator/finance'
const props = defineProps({
  campaignData: {
    type: Object,
    default: () => ({})
  },
  // 模态框标题
  title: {
    type: String,
    default: 'Payment Info'
  },
  // 模态框宽度
  width: {
    type: [String, Number],
    default: 500
  },
  // 金额标签
  amountLabel: {
    type: String,
    default: 'Payment Amount'
  },
  // 金额占位符
  amountPlaceholder: {
    type: String,
    default: 'Enter payment amount'
  },
  // 日期标签
  dateLabel: {
    type: String,
    default: 'Payment Date'
  },
  // 日期占位符
  datePlaceholder: {
    type: String,
    default: 'Select payment date'
  },
  // 取消按钮文本
  cancelText: {
    type: String,
    default: 'Cancel'
  },
  // 提交按钮文本
  submitText: {
    type: String,
    default: 'Submit'
  },
  // 是否自动调用API
  autoSubmit: {
    type: Boolean,
    default: true
  },
  // 自定义API方法
  submitApi: {
    type: Function,
    default: null
  },
  // 成功消息
  successMessage: {
    type: String,
    default: 'Payment submitted successfully'
  },
  // 错误消息
  errorMessage: {
    type: String,
    default: 'Failed to submit payment'
  }
})

const emit = defineEmits(['submit', 'success', 'error', 'cancel'])

const visible = ref(false)
const loading = ref(false)
const amountError = ref('')
const dateError = ref('')
const campaignId = ref('')
const form = reactive({
  paymentAmount: '',
  paymentDate: null
})

// 监听visible变化，重置表单
watch(visible, (newVal) => {
  if (newVal) {
    resetForm()
  }
})

const resetForm = () => {
  form.paymentAmount = ''
  form.paymentDate = null
  amountError.value = ''
  dateError.value = ''
}

const validateAmount = () => {
  if (!form.paymentAmount || form.paymentAmount <= 0) {
    amountError.value = 'Please enter a valid payment amount'
    return false
  }
  amountError.value = ''
  return true
}

const validateDate = () => {
  if (!form.paymentDate) {
    dateError.value = 'Please select a payment date'
    return false
  }
  dateError.value = ''
  return true
}

const handleSubmit = async () => {
  const isAmountValid = validateAmount()
  const isDateValid = validateDate()
  
  if (!isAmountValid || !isDateValid) {
    return
  }
  
  loading.value = true
  
  try {
    // 将日期转换为时间戳
    const paymentTimestamp = form.paymentDate ? new Date(form.paymentDate).getTime() : null
    
    const paymentData = {
      paymentAmount: form.paymentAmount,
      paymentDate: paymentTimestamp, // 使用时间戳
      campaignId: campaignId.value,
    }
    const data = await financeApi.campaignsPayment(paymentData)
   
    if (data) {
        message.success('Payment submitted successfully')
        emit('submit')
    visible.value = false

      }else{
        message.error(data.error)
      }
   
    // 触发提交事件，让父组件处理
    
    
  
    
  } catch (error) {
    message.error(props.errorMessage)
    emit('error', error)
    console.error('Payment error:', error)
  } finally {
    loading.value = false
  }
}

const handleCancel = () => {
  visible.value = false
}

// 使用 defineExpose 暴露方法给父组件
const show = () => {
  visible.value = true
}

const hide = () => {
  visible.value = false
}

const open = (campaignIdValue) => {
  resetForm()
  campaignId.value = campaignIdValue
  visible.value = true
}

// 暴露方法给父组件使用
defineExpose({
  show,
  hide,
  open
})
</script>

<style lang="less" scoped>
.payment-form {
  padding: 8px 0;
  
  .form-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
  }
}

:deep(.ant-form-item-label) {
  font-weight: 500;
  color: #262626;
  font-size: 14px;
}

:deep(.ant-form-item-explain-error) {
  font-size: 12px;
  margin-top: 4px;
  color: #ff4d4f;
}

:deep(.artmisads-input) {
  width: 100%;
}

:deep(.artmisads-date-picker) {
  width: 100%;
}

:deep(.artmisads-button) {
  height: 40px;
  border-radius: 8px;
  font-weight: 500;
  
  &.ant-btn-primary {
    background-color: #6c5ce7;
    border-color: #6c5ce7;
    
    &:hover {
      background-color: #5f3dc4;
      border-color: #5f3dc4;
    }
  }
}
</style>